import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
    Tooltip
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import {
    CANCELLATION_CATEGORY,
    DEFAULT_PAYMENT_DUE,
    PAYMENT_DUE_OPTIONS,
    PAYMENT_TYPE_OPTIONS,
    CATEGORY_OPTIONS,
    CANCELLATION_TYPE_OPTIONS,
    REFUND_METHOD_OPTIONS,
    STATUS_OPTIONS,
    getCancellationTypeLabel,
    hasValue,
    toOptions,
} from 'utils/paymentTermConstants';
import { UiDetailSection } from 'components/v2/record/DetailSection';

const NUMERIC_FIELDS = ['days', 'amount_percent', 'amount_actual', 'coupon_expiry_days'];

// Cleared numeric inputs come back as '' - the API expects null (or a number)
const normalizePayload = (fields, category) => {
    const payload = { ...fields };
    NUMERIC_FIELDS.forEach((key) => {
        if (key in payload) {
            payload[key] = hasValue(payload[key]) ? Number(payload[key]) : null;
        }
    });
    // Refund Term doesn't apply to cancellation rules
    if (category === CANCELLATION_CATEGORY) {
        delete payload.refund_term;
    }
    return payload;
};

// Mirrors the backend rules so the user gets feedback before the request is sent
const validateCancellationTerm = (term) => {
    if (term.category !== CANCELLATION_CATEGORY) return null;
    const errors = [];
    if (!term.cancellation_type) errors.push('Cancellation Type is required');
    if (!term.refund_method) errors.push('Refund Method is required');
    if (term.refund_method === 'Coupon') {
        if (!hasValue(term.coupon_expiry_days)) {
            errors.push('Coupon Expiry (days) is required when Refund Method is Coupon');
        } else if (!Number.isInteger(Number(term.coupon_expiry_days)) || Number(term.coupon_expiry_days) <= 0) {
            errors.push('Coupon Expiry (days) must be a whole number greater than 0');
        }
    }
    if (!hasValue(term.days)) errors.push('Days is required');
    if (!hasValue(term.amount_percent)) {
        errors.push('Amount (%) is required');
    } else if (Number(term.amount_percent) < 0 || Number(term.amount_percent) > 100) {
        errors.push('Percentage must be between 0 and 100');
    }
    return errors.length ? errors.join('; ') : null;
};

const PaymentTermDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [term, setTerm] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchTermData();
    }, [id]);

    const fetchTermData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/payment-terms/${id}`);
            setTerm(response.data);
        } catch (error) {
            console.error('Error fetching payment term data:', error);
            setSnackbar({ open: true, message: 'Error loading payment term details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setTerm(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }

        const validationError = validateCancellationTerm(term);
        if (validationError) {
            setSnackbar({ open: true, message: validationError, severity: 'error' });
            return;
        }

        try {
            await axiosInstance.put(
                `/payment-terms/${id}`,
                normalizePayload(updatedFields, term.category)
            );
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Payment Term updated successfully', severity: 'success' });
            fetchTermData();
        } catch (error) {
            console.error('Error updating payment term:', error);
            const detail = error.response?.data?.detail;
            setSnackbar({
                open: true,
                message: (typeof detail === 'string' && detail) || 'Error updating payment term',
                severity: 'error'
            });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this payment term?')) {
            try {
                await axiosInstance.delete(`/payment-terms/${id}`);
                setSnackbar({ open: true, message: 'Payment Term deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/payment-terms'), 1000);
            } catch (error) {
                console.error('Error deleting payment term:', error);
                setSnackbar({ open: true, message: 'Error deleting payment term', severity: 'error' });
            }
        }
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((item) => (
                    <Grid item xs={12} md={6} key={item.field}>
                        <InlineEditField
                            label={item.label}
                            value={term?.[item.field]}
                            options={item.options || null}
                            onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                            isEditing={isEditing}
                            readOnly={item.readOnly || false}
                            renderValue={item.renderValue || ((val) => (val ? String(val) : '-'))}
                        />
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox
                mb={3}
                p={3}
                borderRadius="lg"
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: 3
                }}
            >
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <Skeleton variant="rectangular" height={400} />
                </MDBox>

            </DashboardLayout>
        );
    }

    if (!term) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <MDTypography>Payment Term not found</MDTypography>
                </MDBox>

            </DashboardLayout>
        );
    }

    // Reacts live to the Category being edited inline
    const isCancellation = term.category === CANCELLATION_CATEGORY;

    const basicFields = [
        { label: "Name", field: "name" },
        { label: "Category", field: "category", options: toOptions(CATEGORY_OPTIONS) },
        { label: "Status", field: "status", options: toOptions(STATUS_OPTIONS) },
        isCancellation
            ? {
                label: "Refund Condition",
                field: "payment_due",
                options: [{ value: DEFAULT_PAYMENT_DUE, label: DEFAULT_PAYMENT_DUE }]
            }
            : { label: "Payment Due", field: "payment_due", options: toOptions(PAYMENT_DUE_OPTIONS) },
        ...(isCancellation
            ? []
            : [{ label: "Payment Type", field: "payment_type", options: toOptions(PAYMENT_TYPE_OPTIONS) }]),
        { label: "Days", field: "days", renderValue: (val) => hasValue(val) ? String(val) : '-' },
        { label: "Amount (%)", field: "amount_percent", renderValue: (val) => hasValue(val) ? `${val}%` : '-' },
        // Cancellation rules are percentage-only, so Amount is hidden for them
        ...(isCancellation
            ? []
            : [{ label: "Amount ($)", field: "amount_actual", renderValue: (val) => hasValue(val) ? `$${val}` : '-' }]),
        ...(isCancellation
            ? []
            : [{
                label: "Refund Term",
                field: "refund_term",
                options: [
                    { value: "Per Pax", label: "Per Pax" },
                    { value: "Per Qty", label: "Per Qty" }
                ]
            }]),
        // Applicable For doesn't apply to cancellation rules
        ...(isCancellation
            ? []
            : [{ label: "Applicable For", field: "applicable_for_id", readOnly: true, renderValue: (val) => term.applicable_for?.name || val || '-' }]),
    ];

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">
                                        Payment Term #{term.id}
                                    </MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>
                                        Payment Term Details
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <Tooltip title="Cancel">
                                                <IconButton
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setUpdatedFields({});
                                                        fetchTermData();
                                                    }}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton
                                                    onClick={handleSaveChanges}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <Tooltip title="Edit Payment Term">
                                            <IconButton
                                                onClick={() => setIsEditing(true)}
                                                sx={commonIconButtonStyles}
                                            >
                                                <Edit sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Delete Payment Term">
                                        <IconButton
                                            onClick={handleDelete}
                                            sx={commonIconButtonStyles}
                                        >
                                            <Delete sx={commonIconStyles} />
                                        </IconButton>
                                    </Tooltip>
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Basic Information", basicFields)}

                                {isCancellation && renderSection("Cancellation Settings", [
                                    {
                                        label: "Cancellation Type",
                                        field: "cancellation_type",
                                        options: CANCELLATION_TYPE_OPTIONS,
                                        renderValue: (val) => val ? getCancellationTypeLabel(val) : '-'
                                    },
                                    { label: "Refund Method", field: "refund_method", options: toOptions(REFUND_METHOD_OPTIONS) },
                                    // Only relevant for the Coupon refund method
                                    ...(term.refund_method === 'Coupon'
                                        ? [{
                                            label: "Coupon Expiry (days)",
                                            field: "coupon_expiry_days",
                                            renderValue: (val) => hasValue(val) ? `${val} days` : '-'
                                        }]
                                        : []),
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name", readOnly: true },
                                    { label: "Modified By", field: "updated_by_name", readOnly: true },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default PaymentTermDetail;